import { Controller } from '@hotwired/stimulus';
import { Modal } from 'bootstrap';

export default class extends Controller {

    static targets = [
        'modal',
        'modalTitle',
        'tag',
        'title',
        'description',
        'date',
        'daysBefore',
        'user',
    ];

    static values = {
        url: String,
        deleteUrl: String,
        editUrl: String,
    };

    declare readonly urlValue: string;
    declare readonly deleteUrlValue: string;
    declare readonly editUrlValue: string;

    declare readonly modalTarget: HTMLElement;
    declare readonly modalTitleTarget: HTMLElement;
    declare readonly tagTarget: HTMLSelectElement;
    declare readonly titleTarget: HTMLInputElement;
    declare readonly descriptionTarget: HTMLTextAreaElement;
    declare readonly dateTarget: HTMLInputElement;
    declare readonly daysBeforeTarget: HTMLInputElement;
    declare readonly userTarget: HTMLSelectElement;

    modal!: Modal;
    editingId: string | null = null;

    connect(): void {

        this.modal = new Modal(
            this.modalTarget
        );
    }

    openModal(): void {
        this.editingId = null;
        this.modalTitleTarget.textContent = 'Nuevo recordatorio';
        this.resetForm();
        this.modal.show();
    }

    openEditModal(event: Event): void {
        const button = event.currentTarget as HTMLElement;
        const card = button.closest('.reminder-card') as HTMLElement;

        if (!card) {
            return;
        }

        this.editingId = card.dataset.reminderId ?? null;
        this.modalTitleTarget.textContent = 'Editar recordatorio';

        const tagId = card.dataset.reminderTagId ?? '';
        const title = card.dataset.reminderTitle ?? '';
        const description = card.dataset.reminderDescription ?? '';
        const date = card.dataset.reminderDate ?? '';
        const daysBefore = card.dataset.reminderDaysBefore ?? '0';
        const userId = card.dataset.reminderUserId ?? '';

        if (tagId) {
            this.tagTarget.value = tagId;
        }
        this.titleTarget.value = title;
        this.descriptionTarget.value = description;
        this.setDate(date);
        this.daysBeforeTarget.value = daysBefore;
        if (userId) {
            this.userTarget.value = userId;
        }

        this.modal.show();
    }

    closeModal(): void {

        this.modal.hide();
    }

    async save(): Promise<void> {
        const payload = {
            tag: this.tagTarget.value,
            title: this.titleTarget.value,
            description: this.descriptionTarget.value,
            reminderDate: this.dateTarget.value,
            daysBefore: this.daysBeforeTarget.value,
            user: this.userTarget.value,
        };

        if (this.editingId) {
            await this.update(payload);
        } else {
            await this.create(payload);
        }
    }

    async create(payload: object): Promise<void> {
        const response = await fetch(
            this.urlValue,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
            }
        );

        const json = await response.json();

        if (!json.success) {
            return;
        }

        this.appendReminderCard(json.reminder);
        this.resetForm();
        this.closeModal();
    }

    async update(payload: object): Promise<void> {
        const url = this.editUrlValue.replace('/0', '/' + this.editingId);

        const response = await fetch(
            url,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
            }
        );

        const json = await response.json();

        if (!json.success) {
            return;
        }

        this.updateReminderCard(json.reminder);
        this.resetForm();
        this.closeModal();
    }

    async deleteReminder(
        event: Event
    ): Promise<void> {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement;
        const id = button.dataset.id;
        if (!id) {
            return;
        }
        const response = await fetch(
            this.deleteUrlValue.replace(
                '/0',
                '/' + id
            ),
            {
                method: 'DELETE',

                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            }
        );

        const json = await response.json();
        if (!json.success) {
            return;
        }

        const card = button.closest(
            '.reminder-card'
        );

        if (card) {
            card.remove();
        }
    }

    setDate(date: string): void {
        const fp = (this.dateTarget as any)._flatpickr;
        if (fp) {
            fp.setDate(date, true);
        } else {
            this.dateTarget.value = date;
        }
    }

    resetForm(): void {
        this.titleTarget.value = '';
        this.descriptionTarget.value = '';
        this.setDate('');
        this.daysBeforeTarget.value = '0';
        this.editingId = null;
    }

    appendReminderCard(
        reminder: any
    ): void {
        const container = window.document.getElementById(
            'reminders-grid'
        );
        if (!container) {
            return;
        }

        container.insertAdjacentHTML(
            'beforeend',
            this.buildCardHtml(reminder)
        );
    }

    updateReminderCard(reminder: any): void {
        const card = this.element.querySelector(
            `.reminder-card[data-reminder-id="${reminder.id}"]`
        ) as HTMLElement;

        if (!card) {
            return;
        }

        card.dataset.reminderTagId = reminder.tagId ?? '';
        card.dataset.reminderTitle = reminder.title;
        card.dataset.reminderDescription = reminder.description ?? '';
        card.dataset.reminderDate = reminder.dateRaw;
        card.dataset.reminderDaysBefore = String(reminder.daysBefore);
        card.dataset.reminderUserId = String(reminder.userId);

        const newCard = document.createElement('div');
        newCard.innerHTML = this.buildCardHtml(reminder);
        const inner = newCard.querySelector('.reminder-card') as HTMLElement;

        if (inner) {
            card.innerHTML = inner.innerHTML;
            Object.assign(card.dataset, inner.dataset);
        }
    }

    buildCardHtml(reminder: any): string {
        const badge = reminder.tag
            ? `<span class="badge bg-${reminder.tagColor}">${reminder.tag}</span>`
            : '';

        return `
            <div class="col-md-4 mb-3 reminder-card"
                 data-reminder-id="${reminder.id}"
                 data-reminder-tag-id="${reminder.tagId ?? ''}"
                 data-reminder-title="${reminder.title}"
                 data-reminder-description="${reminder.description ?? ''}"
                 data-reminder-date="${reminder.dateRaw}"
                 data-reminder-days-before="${reminder.daysBefore}"
                 data-reminder-user-id="${reminder.userId}">

                <div class="card h-100">

                    <div class="card-body">

                        <div class="mb-2">
                            ${badge}
                        </div>

                        <h5 class="mb-2">
                            ${reminder.title}
                        </h5>

                        <div class="text-muted small mb-2">
                            ${reminder.date}
                        </div>

                        <div class="small mb-3">
                            Avisar ${reminder.daysBefore} días antes
                        </div>

                        <div class="small mb-3">
                            Usuario:
                            ${reminder.user}
                        </div>

                        <div class="d-flex gap-2">

                            <button
                                type="button"
                                class="btn btn-outline-secondary btn-sm"
                                data-action="reminder#openEditModal"
                            >
                                Editar
                            </button>

                            <button
                                type="button"
                                class="btn btn-danger btn-sm"
                                data-id="${reminder.id}"
                                data-action="reminder#deleteReminder"
                            >
                                Eliminar
                            </button>

                        </div>

                    </div>

                </div>

            </div>
        `;
    }
}
